import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ClipboardList, RefreshCcw, Eye, Users, Layers, ShieldCheck } from "lucide-react";
import MarketingLayout from "../components/marketing/MarketingLayout";
import { LineMask, FadeIn, SectionTag } from "../components/marketing/Reveal";

const SERVICES = [
  {
    n: "01", icon: ClipboardList, title: "Full Treatment Planning",
    body: "End-to-end clear-aligner treatment planning from your records: clinical assessment, digital strategy, staging, attachments, IPR and occlusion — delivered doctor-ready.",
    tags: ["Staging & biomechanics", "Attachments & IPR", "Platform-independent"],
  },
  {
    n: "02", icon: RefreshCcw, title: "Refinement & Mid-Course Planning",
    body: "Refinement setups, mid-course corrections and additional aligner stages — planned with the same clinical rigor as the initial case.",
    tags: ["Refinements", "Corrections", "Overcorrection staging"],
  },
  {
    n: "03", icon: Eye, title: "Setup Review & Second Opinion",
    body: "An experienced clinical review of an existing digital setup before you approve it — flagging unrealistic movements, staging issues and biomechanical risks.",
    tags: ["Quality assurance", "Risk flagging", "Approval support"],
  },
  {
    n: "04", icon: Users, title: "Planning Team Extension",
    body: "A dedicated planning pod that works as an extension of your clinic — aligned to your treatment philosophy, preferences and protocols.",
    tags: ["Dedicated planners", "Your protocols", "Consistent style"],
  },
  {
    n: "05", icon: Layers, title: "Lab & Aligner Company Overflow",
    body: "Scalable planning capacity for dental laboratories and aligner companies — absorb volume spikes without growing headcount.",
    tags: ["Volume support", "White-label ready", "SLA turnaround"],
  },
  {
    n: "06", icon: ShieldCheck, title: "Case Triage & Complexity Screening",
    body: "Rapid assessment of case suitability, complexity and limitations — so you set the right expectations before treatment begins.",
    tags: ["Suitability screening", "Limitation notes", "Expectation setting"],
  },
];

export default function Services() {
  return (
    <MarketingLayout>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
        <section className="pt-40 pb-16 lg:pb-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <SectionTag>Services</SectionTag>
            <h1 className="font-display font-black tracking-tight leading-[0.98] text-[clamp(2.6rem,6.5vw,5.5rem)] max-w-4xl">
              <LineMask delay={0.1}>Planning services,</LineMask>
              <LineMask delay={0.25} className="text-neon">built for professionals.</LineMask>
            </h1>
            <FadeIn delay={0.5}>
              <p className="mt-8 max-w-2xl text-base md:text-lg text-slate-400 leading-relaxed">
                For general dentists, orthodontists, clinics, laboratories and aligner companies — from single cases to dedicated planning capacity.
              </p>
            </FadeIn>
          </div>
        </section>

        <section className="pb-24 lg:pb-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-5 lg:gap-7">
            {SERVICES.map((s, i) => (
              <FadeIn key={s.n} delay={(i % 2) * 0.08}>
                <div className="group relative h-full bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 lg:p-10 overflow-hidden transition-all duration-500 hover:border-neon/40 hover:bg-white/[0.07]">
                  <span className="absolute -top-4 -right-2 font-display font-black text-8xl text-neon/[0.07] group-hover:text-neon/15 transition-colors duration-500 select-none" aria-hidden="true">{s.n}</span>
                  <s.icon className="h-8 w-8 text-neon mb-6 transition-transform duration-500 group-hover:scale-110" />
                  <h2 className="font-display font-bold text-2xl tracking-tight">{s.title}</h2>
                  <p className="mt-4 text-sm text-slate-400 leading-relaxed">{s.body}</p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {s.tags.map((t) => (
                      <span key={t} className="rounded-full border border-white/15 px-3 py-1 text-[11px] font-medium text-slate-300">{t}</span>
                    ))}
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn>
            <div className="mt-16 text-center">
              <Link to="/register" data-testid="services-cta-btn"
                className="group inline-flex items-center gap-2 rounded-full bg-neon px-8 py-4 text-sm font-bold text-ink transition-transform duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(0,229,255,0.45)]">
                Submit your first case <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </FadeIn>
        </section>
      </motion.div>
    </MarketingLayout>
  );
}
